"use client";

import { useEffect, useCallback, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { useStore } from "../store";
import {
  useChannelMessages,
  useMessagesLoading,
  useMessagesFetchId,
  useHasMoreMessages,
  useOldestMessageCursor,
} from "../store/selectors";
import type { ChannelEvent } from "../store/types";

export type { ChannelEvent } from "../store/types";

const PAGE_SIZE = 100;

export function useMessages(channelId: string | null) {
  const { authenticated, token } = useAuth();
  const messages = useChannelMessages(channelId);
  const loading = useMessagesLoading(channelId);
  const fetchId = useMessagesFetchId(channelId);
  const hasMore = useHasMoreMessages(channelId);
  const oldestCursor = useOldestMessageCursor(channelId);
  const channelRef = useRef(channelId);
  const tokenRef = useRef(token);
  const loadingOlderRef = useRef(false);
  tokenRef.current = token;

  useEffect(() => {
    channelRef.current = channelId;
    loadingOlderRef.current = false;
  }, [channelId]);

  const fetchMessages = useCallback(async () => {
    const t = tokenRef.current;
    if (!authenticated || !channelId || !t) {
      return;
    }

    useStore.getState().setMessagesLoading(channelId, true);
    try {
      const data = await apiFetch<ChannelEvent[]>(
        `/channels/${channelId}/events?limit=${PAGE_SIZE}`,
        t
      );
      if (channelRef.current === channelId) {
        useStore.getState().setMessages(channelId, data, data.length >= PAGE_SIZE);
      }
    } catch {
      useStore.getState().setMessagesLoading(channelId, false);
    }
  }, [authenticated, channelId]);

  useEffect(() => {
    if (channelId) {
      fetchMessages();
    }
  }, [channelId, fetchMessages]);

  const fetchOlderMessages = useCallback(async () => {
    const t = tokenRef.current;
    const cid = channelRef.current;
    if (!cid || !t || loadingOlderRef.current) return;

    // Read latest cursor from store (not stale closure)
    const state = useStore.getState();
    const bucket = state.messages.byChannel[cid];
    if (!bucket || !bucket.hasMore || !bucket.oldestCursor) return;

    loadingOlderRef.current = true;
    try {
      const data = await apiFetch<ChannelEvent[]>(
        `/channels/${cid}/events?before=${bucket.oldestCursor}&limit=${PAGE_SIZE}`,
        t
      );
      if (channelRef.current === cid) {
        useStore.getState().prependMessages(cid, data, data.length >= PAGE_SIZE);
      }
    } catch {
      // silently fail — user can scroll up again to retry
    } finally {
      loadingOlderRef.current = false;
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string, attachmentIds?: string[]) => {
      const t = tokenRef.current;
      if (!channelId || !t) return;
      const body: Record<string, unknown> = {};
      if (text) body.text = text;
      if (attachmentIds?.length) body.attachments = attachmentIds.map(id => ({ id }));
      await apiFetch(`/channels/${channelId}/messages`, t, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    [channelId]
  );

  const appendMessage = useCallback(
    (event: ChannelEvent) => {
      useStore.getState().appendMessage(event);
    },
    []
  );

  return {
    messages,
    loading,
    hasMore,
    sendMessage,
    appendMessage,
    fetchOlderMessages,
    refetch: fetchMessages,
    fetchId,
  };
}
